import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { Info, CheckCircle, AlertTriangle, XCircle, X, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Banner>. Layout, the
// variant→role branching, and CSS ported letter-for-letter from the
// vendor's compiled bundle and Banner-77XJS2VT.css — new .rc-banner class
// names. `Banner.Skeleton` (the vendor's compound sub-component) has zero
// real call sites in this app, so it isn't ported.
//
// Icons switched from @datasirpi/icons to lucide-react — design.md calls
// for Lucide icons app-wide, and lucide is already a direct dependency
// used throughout the app, so this isn't a new dependency.
//
// The vendor icon carried its own `role="img"` + accessible label (e.g.
// "Warning") via its own IconBase, which lucide's icons don't replicate.
// That label was always redundant here: the banner's title/body text is
// always visible, and the outer `role="alert"`/`"status"` already
// announces the region — so the icon is marked `aria-hidden` instead of
// reproducing a second, duplicate announcement.
export type BannerVariant = "info" | "success" | "warning" | "danger";

export interface BannerProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  variant?: BannerVariant;
  title?: ReactNode;
  children: ReactNode;
  /* Phase 4.9 — the action a banner is usually asking for. A banner that
     says "3 collectors are failing" and gives you no way to go and look at
     them makes the reader do the navigation themselves; 42 call sites had
     been hand-rolling a link into the body text to work around this. */
  action?: ReactNode;
  /** Renders a close button. The caller owns the dismissed state. */
  onDismiss?: () => void;
  /** Accessible name for the close button. */
  dismissLabel?: string;
}

const ICON_FOR: Record<BannerVariant, LucideIcon> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  danger: XCircle,
};

export const Banner = forwardRef<HTMLDivElement, BannerProps>(function Banner(
  { variant = "info", title, className, children, action, onDismiss, dismissLabel = "Dismiss", ...rest },
  ref
) {
  const Icon = ICON_FOR[variant];

  return (
    <div
      ref={ref}
      /* Phase 4.9 — `alert` is reserved for danger now.
         `role="alert"` is an assertive live region: it interrupts whatever a
         screen reader is currently saying. Warnings are routinely rendered
         on page load in this app ("this tenant is in trial", "2 collectors
         degraded"), so every such page began by talking over itself before
         the user had read anything. Only `danger` — which does mean "stop
         and look now" — keeps the interruption; warning drops to the polite
         `status` region, which is announced at the next natural pause. */
      role={variant === "danger" ? "alert" : "status"}
      className={cn("rc-banner", `rc-banner--${variant}`, className)}
      {...rest}
    >
      <Icon size="1em" aria-hidden="true" className="rc-banner__icon" />
      <div className="rc-banner__content">
        {title ? <p className="rc-banner__title">{title}</p> : null}
        {/* A <div>, not a <p>. The body was wrapped in a paragraph, so any
            call site passing a list, a table or a second paragraph produced
            invalid nesting that browsers silently reflow — the content
            escaped the <p> and lost the banner's own layout. */}
        <div className="rc-banner__body">{children}</div>
        {action ? <div className="rc-banner__action">{action}</div> : null}
      </div>
      {onDismiss ? (
        <button type="button" className="rc-banner__dismiss" aria-label={dismissLabel} onClick={onDismiss}>
          <X size={14} aria-hidden />
        </button>
      ) : null}
    </div>
  );
});
